import {
  fromLocalToDBTimestamp,
  completionTimeBuffer,
  getDateTime,
} from '@pinnacleautomotive/clarity-components';
import { createSelector } from '@reduxjs/toolkit';
import isNull from 'lodash.isnull';
import omitBy from 'lodash.omitby';

const selectServiceOrderData = state => state.newServiceOrder;

export const selectServiceOrderSelectedVendor = createSelector(
  selectServiceOrderData,
  state => state.selectedVendor
);

export const selectServiceOrderSelectedDepartment = createSelector(
  selectServiceOrderData,
  state => state.selectedDepartment
);

export const selectServiceOrderSelectedPrices = createSelector(
  selectServiceOrderData,
  state => state.selectedPrices
);

export const selectServiceOrderSelectedCategories = createSelector(
  selectServiceOrderData,
  state => state.selectedCategories
);

export const selectServiceOrderSelectedPricesWithDetails = createSelector(
  selectServiceOrderData,
  state =>
    state.prices.filter(price =>
      state.selectedPrices.includes(price.servicePriceId)
    )
);

export const selectServiceOrderPrices = createSelector(
  selectServiceOrderData,
  state => state.prices
);

export const selectServiceOrderSelectedVehicle = createSelector(
  selectServiceOrderData,
  state => state.selectedVehicle
);

export const selectServiceOrderVehicleNotFound = createSelector(
  selectServiceOrderData,
  state => state.vehicleNotFound
);

export const selectServiceOrderEditVehicleModalOpen = createSelector(
  selectServiceOrderData,
  state => state.editVehicleModalOpen
);

export const selectServiceOrderObservations = createSelector(
  selectServiceOrderData,
  state => state.observation
);

export const selectServiceOrderPricesGrouped = createSelector(
  selectServiceOrderPrices,
  state =>
    state.reduce(
      (prev, actual) => ({
        ...prev,
        [actual.service.type]: [...(prev[actual.service.type] || []), actual],
      }),
      {}
    )
);

export const selectServiceOrderIsLoading = createSelector(
  selectServiceOrderData,
  state => state.isLoading
);

export const selectServiceOrderIsLoadingServices = createSelector(
  selectServiceOrderData,
  state => state.isLoadingServices
);

export const selectServiceOrderIsLoadingVehicle = createSelector(
  selectServiceOrderData,
  state => state.isLoadingVehicle
);

export const selectServiceOrderDiscarded = createSelector(
  selectServiceOrderData,
  state => state.discarded
);

export const selectServiceOrderSpecialRequests = createSelector(
  selectServiceOrderData,
  state => state.specialRequests
);

export const selectedServiceOrderAlternativeOwner = createSelector(
  selectServiceOrderData,
  state => state.alternativeOwner
);

export const selectServiceOrderAlternativeOwnerLoading = createSelector(
  selectServiceOrderData,
  state => state.alternativeOwnerLoading
);

export const selectServiceOrderAlternativeOwner = createSelector(
  selectServiceOrderData,
  state => state.owners
);
export const selectServiceOrderCompletionType = createSelector(
  selectServiceOrderData,
  state => state.completionType
);

export const selectServiceOrderEstimatedCompletion = createSelector(
  selectServiceOrderData,
  state =>
    getDateTime(new Date(state.createdDate))
      .plus({ minutes: state.servicesTotalComplianceTime })
      .plus({ minutes: completionTimeBuffer })
);

export const selectServiceOrderPromiseDate = createSelector(
  selectServiceOrderData,
  state => new Date(state.promiseDate)
);

export const selectServiceOrderSaveError = createSelector(
  selectServiceOrderData,
  state => state.saveError
);

export const selectServiceOrderWorkOrderSaved = createSelector(
  selectServiceOrderData,
  state => state.workOrderSaved
);

export const selectServiceOrderDataToSave = createSelector(
  selectServiceOrderData,
  state =>
    omitBy(
      {
        vehicleId: state.selectedVehicle?.vehicleId,
        priceIds: state.selectedPrices,
        dealerPurchaseOrderNumber: state.po,
        stockNumber: state.stockNumber,
        repairOrder: state.ro,
        observation: state.observation,
        specialRequests: state.specialRequests,
        promiseDate: getServiceOrderPromiseDate(state),
        ownerId: state.alternativeOwner?.personId,
        estimations: state.selectedServicesPrices?.map(price => ({
          servicePriceId: price.servicePriceId,
          price: price.price,
          quantity: price.quantity,
          notes: price.comments,
          attachments: price.files.map(file => file.fileId),
        })),
      },
      isNull
    )
);

export const selectServiceOrderEditVehicleDataError = createSelector(
  selectServiceOrderData,
  state => state.editVehicleDataError
);

export const getServiceOrderSelectedServicesPrices = createSelector(
  selectServiceOrderData,
  state => state.selectedServicesPrices
);

export const getServiceOrderSelectedServicesPricesTotalValue = createSelector(
  selectServiceOrderData,
  state =>
    state?.selectedServicesPrices?.reduce(
      (total, actual) => total + actual.amount,
      0
    )
);

export const getServiceOrderSelectedServicesQuantity = createSelector(
  selectServiceOrderData,
  state => state.selectedServicesPrices?.length
);

export const getAllServiceOrderDealers = createSelector(
  selectServiceOrderData,
  state => state.dealers
);

function getServiceOrderPromiseDate(state) {
  return state.completionType === 'promised'
    ? fromLocalToDBTimestamp(new Date(state.promiseDate))
    : null;
}

export const serviceOrderVehicleId = createSelector(
  selectServiceOrderData,
  state => state.vehicleId
);
